import React from 'react';
import { useAuth } from '../../contexts/AuthContext';

const defaultProctoringState = {
  enableFullScreenMonitoring: true,
  fullScreenToleranceLimit: 3,
  enableTabSwitchMonitoring: true,
  tabSwitchToleranceLimit: 2,
  enableCopyPasteBlocking: true,
  copyPasteToleranceLimit: 1,
  enableMouseMonitoring: true,
  mouseMonitoringToleranceLimit: 2,
  enableCameraMonitoring: false,
};

/**
 * Presentational, controlled proctoring settings step.
 * All data loading/saving is handled by the parent modal.
 */
const AssessmentProctoringStep = ({ value, onChange }) => {
  const { user } = useAuth();
  const state = value || defaultProctoringState;
  const isVideoProctoringEnabled = user?.features?.assessment_video_proctoring === true;

  const toggleBaseClass =
    'inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full p-0.5 cursor-pointer border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#007BFF] focus:ring-offset-2';
  const knobClass =
    'h-5 w-5 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out';

  const handleToggle = (key) => {
    if (typeof onChange === 'function') {
      onChange({
        ...state,
        [key]: !state[key],
      });
    }
  };

  const OptionCard = ({ title, description, checked, onClick }) => (
    <button
      type="button"
      className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-gray-200/80 bg-white hover:border-[#007BFF]/30 hover:bg-[#007BFF]/[0.02] transition-all duration-200 cursor-pointer group text-left"
      onClick={onClick}
      aria-pressed={checked}
    >
      <div className="min-w-0 flex-1">
        <span className="text-sm font-semibold text-gray-900 block">{title}</span>
        {description && <span className="text-xs text-gray-500 mt-0.5 block">{description}</span>}
      </div>
      <span
        className={`${toggleBaseClass} ${
          checked ? 'bg-[#007BFF] justify-end' : 'bg-gray-200 justify-start'
        }`}
      >
        <span
          className={`${knobClass} ${checked ? 'translate-x-0' : 'translate-x-0'}`}
          aria-hidden
        />
      </span>
    </button>
  );

  const LockedCard = ({ title, description }) => (
    <div
      className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50 transition-all duration-200 select-none text-left"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-400 block">{title}</span>
          <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-gray-500 bg-gray-100 border border-gray-200">
            <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            LOCKED
          </span>
        </div>
        {description && <span className="text-xs text-gray-400 mt-0.5 block">{description}</span>}
      </div>
      <button
        type="button"
        onClick={() => {
          const orgName = user?.company || user?.name || 'Our Organisation';
          const email = 'sales@talentcrux.ai';
          const subject = encodeURIComponent(`Request Activation: Video Proctoring for Assessments — ${orgName}`);
          const body = encodeURIComponent(
            `Hello TalentCrux Sales Team,\n\nI would love to activate the premium Assessment Video Proctoring feature for our organisation (${orgName}). Please send us details on how to get started!\n\nBest regards,\n${user?.name || 'Recruiting Team'}`
          );
          window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
        }}
        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 border border-gray-200 hover:text-gray-900 rounded-lg text-xs font-semibold tracking-wide transition duration-150 cursor-pointer shrink-0"
      >
        Contact Sales to Activate
      </button>
    </div>
  );

  return (
    <div className="max-w-4xl">
      <div className="space-y-6">
        {/* Basic Monitoring */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 pb-1 border-b border-gray-100">
            Basic Monitoring
          </h3>
          <div className="space-y-3">
            <OptionCard
              title="Full Screen Monitoring"
              description="Enforce full-screen during assessment"
              checked={state.enableFullScreenMonitoring}
              onClick={() => handleToggle('enableFullScreenMonitoring')}
            />
            <OptionCard
              title="Tab Switch Monitoring"
              description="Detect when candidates switch tabs"
              checked={state.enableTabSwitchMonitoring}
              onClick={() => handleToggle('enableTabSwitchMonitoring')}
            />
          </div>
        </div>

        {/* Advanced Monitoring */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 pb-1 border-b border-gray-100">
            Advanced Monitoring
          </h3>
          <div className="space-y-3">
            <OptionCard
              title="Copy/Paste Blocking"
              description="Prevent copying and pasting during the test"
              checked={state.enableCopyPasteBlocking}
              onClick={() => handleToggle('enableCopyPasteBlocking')}
            />
            <OptionCard
              title="Mouse Activity Monitoring"
              description="Track when mouse leaves the test area"
              checked={state.enableMouseMonitoring}
              onClick={() => handleToggle('enableMouseMonitoring')}
            />
            {isVideoProctoringEnabled ? (
              <OptionCard
                title="Video Proctoring"
                description="Monitor candidate webcam real-time and analyze face/phone patterns"
                checked={state.enableCameraMonitoring}
                onClick={() => handleToggle('enableCameraMonitoring')}
              />
            ) : (
              <LockedCard
                title="Video Proctoring"
                description="Monitor candidate webcam real-time and analyze face/phone patterns"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { defaultProctoringState };
export default AssessmentProctoringStep;
